import { Button, Form, Input, message } from 'antd';
import React, { useEffect, useState } from 'react';
import './select-product.scss';
import { getCustomerSecret, updateTaxAddress, UpdateTaxAddressRequest } from '@/api/custom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  fetchFeatureFlags,
  selectFeatureFlags,
  selectUser,
} from '@/store/features/userinfo/userinfoSlice';
import config from '@/services/config';
import { Navigate } from 'react-router-dom';
import { AdvisorSignupLayout } from '@/pages/login/AdvisorSignupLayout';
import { getItemWithExpiry } from '@wavvest/shared-utils';
import {
  getStripeCustomerId,
  isFirstBillingSetup,
  requiresBillingSetup,
  requiresTaxAddress,
  userCanManageBilling,
} from './billing-status';

type AddressType = UpdateTaxAddressRequest['address'];
type AddressField = keyof AddressType;

export const TAX_ADDRESS_FIELD_LIMITS: Record<AddressField, number> = {
  line1: 100,
  line2: 100,
  city: 80,
  state: 2,
  country: 2,
  postal_code: 10,
};

const TAX_ADDRESS_FIELD_LABELS: Record<AddressField, string> = {
  line1: 'Street address line 1',
  line2: 'Street address line 2',
  city: 'City',
  state: 'State',
  country: 'Country',
  postal_code: 'Postal code',
};

const TAX_ADDRESS_REQUIRED_MESSAGES: Record<Exclude<AddressField, 'line2'>, string> = {
  line1: 'Please enter your street address',
  city: 'Please enter your city',
  state: 'Please enter your state',
  country: 'Please enter your country',
  postal_code: 'Please enter your postal code',
};

const ADDRESS_LINE_PATTERN = /^[a-zA-Z0-9\s.,#'/-]+$/;
const CITY_PATTERN = /^[a-zA-Z\s.'-]+$/;
const COUNTRY_CODE_PATTERN = /^[A-Z]{2}$/;
const POSTAL_CODE_PATTERN = /^\d{5}(-\d{4})?$/;
const SUPPORTED_TAX_ADDRESS_COUNTRY = 'US';

export const sanitizeTaxAddress = (values: AddressType): AddressType => ({
  line1: values.line1.trim(),
  line2: (values.line2 ?? '').trim(),
  city: values.city.trim(),
  state: values.state.trim().toUpperCase(),
  country: values.country.trim().toUpperCase(),
  postal_code: values.postal_code.trim(),
});

const normalizeTaxAddressValue = (field: AddressField, value: unknown): string => {
  if (typeof value !== 'string') {
    return '';
  }
  const normalizedValue = value.trim();
  return field === 'state' || field === 'country' ? normalizedValue.toUpperCase() : normalizedValue;
};

export const validateTaxAddressField = (field: AddressField, value: unknown): string | null => {
  const normalizedValue = normalizeTaxAddressValue(field, value);
  const isOptionalBlank = field === 'line2' && normalizedValue.length === 0;

  if (isOptionalBlank) {
    return null;
  }

  if (normalizedValue.length === 0) {
    return TAX_ADDRESS_REQUIRED_MESSAGES[field as Exclude<AddressField, 'line2'>];
  }

  if (normalizedValue.length > TAX_ADDRESS_FIELD_LIMITS[field]) {
    return `${TAX_ADDRESS_FIELD_LABELS[field]} must be ${TAX_ADDRESS_FIELD_LIMITS[field]} characters or fewer.`;
  }

  if ((field === 'line1' || field === 'line2') && !ADDRESS_LINE_PATTERN.test(normalizedValue)) {
    return 'Please use letters, numbers, spaces, and common address punctuation.';
  }

  if (field === 'city' && !CITY_PATTERN.test(normalizedValue)) {
    return 'Please enter a valid city.';
  }

  if (
    field === 'state' &&
    (!COUNTRY_CODE_PATTERN.test(normalizedValue) || !US_STATES.includes(normalizedValue))
  ) {
    return 'Please enter a valid 2-letter US state.';
  }

  if (field === 'country' && !COUNTRY_CODE_PATTERN.test(normalizedValue)) {
    return 'Please enter a 2-letter country code.';
  }

  if (field === 'country' && normalizedValue !== SUPPORTED_TAX_ADDRESS_COUNTRY) {
    return 'Please enter US as the 2-letter country code.';
  }

  if (field === 'postal_code' && !POSTAL_CODE_PATTERN.test(normalizedValue)) {
    return 'Please enter a valid ZIP code.';
  }

  return null;
};

const taxAddressRule = (field: AddressField) => ({
  validator: (_: unknown, value: unknown) => {
    const error = validateTaxAddressField(field, value);
    return error ? Promise.reject(new Error(error)) : Promise.resolve();
  },
});

const taxAddressRules = (field: AddressField) => {
  if (field === 'line2') {
    return [taxAddressRule(field)];
  }

  return [
    {
      required: true,
      whitespace: true,
      message: TAX_ADDRESS_REQUIRED_MESSAGES[field],
    },
    taxAddressRule(field),
  ];
};

export const shouldRenderPricingTable = (userSecret: string): boolean =>
  userSecret.trim().length > 0;

const storedUserCanManageBilling = (): boolean => {
  const storedUser = getItemWithExpiry('user');
  if (!storedUser) {
    return false;
  }

  try {
    return userCanManageBilling(JSON.parse(storedUser));
  } catch {
    return false;
  }
};

export const StripeSelectProduct: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [customerSecretFailed, setCustomerSecretFailed] = useState(false);
  const [userSecret, setUserSecret] = useState('');
  const dispatch = useAppDispatch();
  const featureFlags = useAppSelector(selectFeatureFlags);
  const user = useAppSelector(selectUser);

  const needTaxAddress = requiresTaxAddress(featureFlags.stripeInfo);
  const billingSetupRequired = requiresBillingSetup(featureFlags.stripeInfo);
  const firstSetup = isFirstBillingSetup(featureFlags.stripeInfo);
  const canManageBilling = userCanManageBilling(user) || storedUserCanManageBilling();

  const handleSubmitTaxAddress = async (values: AddressType) => {
    setLoading(true);
    const address = sanitizeTaxAddress(values);
    try {
      await updateTaxAddress({ address });
    } catch (e) {
      message.error(`Failed to update tax address: ${e}`);
    }
    dispatch(fetchFeatureFlags(true));
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      if (!canManageBilling || !billingSetupRequired || !firstSetup) {
        return;
      }
      const customerId = getStripeCustomerId(featureFlags.stripeInfo);
      if (customerId === null) {
        return;
      }
      setLoading(true);
      setCustomerSecretFailed(false);
      try {
        const userSecret: string = await getCustomerSecret(customerId);
        setUserSecret(userSecret.trim());
      } catch (e) {
        setCustomerSecretFailed(true);
        message.error(`Failed to fetch customer secret: ${e}. Please contact support`);
      } finally {
        setLoading(false);
      }
    })();
  }, [billingSetupRequired, canManageBilling, featureFlags, firstSetup, setUserSecret]);

  if (!canManageBilling) {
    return <Navigate to="/home" replace />;
  }

  if (!billingSetupRequired) {
    return <Navigate to="/home" />;
  }

  if (featureFlags.loading) {
    return (
      <AdvisorSignupLayout activeStep={4}>
        <div className="advisor-signup-form-block advisor-stripe-content">
          <div className="advisor-signup-copy">
            <h1>Set up billing</h1>
            <p>Loading your billing setup...</p>
          </div>
        </div>
      </AdvisorSignupLayout>
    );
  }

  if (!firstSetup) {
    return <Navigate to="/subscriptions?manage=1" replace />;
  }

  return (
    <AdvisorSignupLayout activeStep={4}>
      <div className="advisor-signup-form-block advisor-stripe-content">
        <div className="advisor-signup-copy">
          <h1>{needTaxAddress ? 'Enter your address' : 'Choose your plan'}</h1>
          <p>
            {needTaxAddress
              ? 'We need this to calculate tax before activating your workspace.'
              : 'Select a plan to activate your advisor portal.'}
          </p>
        </div>
        {needTaxAddress && (
          <Form
            id="tax-address-form"
            className="advisor-signup-form"
            name="tax-address-form"
            layout="vertical"
            onFinish={handleSubmitTaxAddress}
          >
            <Form.Item label="Street Address Line 1" name="line1" rules={taxAddressRules('line1')}>
              <Input maxLength={TAX_ADDRESS_FIELD_LIMITS.line1} />
            </Form.Item>
            <Form.Item label="Street Address Line 2" name="line2" rules={taxAddressRules('line2')}>
              <Input maxLength={TAX_ADDRESS_FIELD_LIMITS.line2} />
            </Form.Item>
            <Form.Item label="City" name="city" rules={taxAddressRules('city')}>
              <Input maxLength={TAX_ADDRESS_FIELD_LIMITS.city} />
            </Form.Item>
            <Form.Item label="State (2-letter form)" name="state" rules={taxAddressRules('state')}>
              <Input maxLength={TAX_ADDRESS_FIELD_LIMITS.state} />
            </Form.Item>
            <Form.Item label="Country" name="country" rules={taxAddressRules('country')}>
              <Input maxLength={TAX_ADDRESS_FIELD_LIMITS.country} />
            </Form.Item>
            <Form.Item
              label="Postal Code"
              name="postal_code"
              rules={taxAddressRules('postal_code')}
            >
              <Input maxLength={TAX_ADDRESS_FIELD_LIMITS.postal_code} />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="advisor-signup-primary-button"
              >
                Set address
              </Button>
            </Form.Item>
          </Form>
        )}
        {!needTaxAddress && !shouldRenderPricingTable(userSecret) && (
          <p>
            {customerSecretFailed
              ? 'Unable to load your billing setup.'
              : 'Loading your billing setup...'}
          </p>
        )}
        {!needTaxAddress && shouldRenderPricingTable(userSecret) && (
          <div className="stripe-container">
            <stripe-pricing-table
              pricing-table-id={config.stripePricingTableId}
              publishable-key={config.stripePublishableKey}
              customer-session-client-secret={userSecret}
            ></stripe-pricing-table>
          </div>
        )}
      </div>
    </AdvisorSignupLayout>
  );
};

const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'AS',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'GU',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'MP',
  'OH',
  'OK',
  'OR',
  'PA',
  'PR',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'TT',
  'UT',
  'VT',
  'VA',
  'VI',
  'WA',
  'WV',
  'WI',
  'WY',
];
